import React from "react";
import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, Box, Paper, Typography, TextField, CircularProgress } from "@mui/material";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import List from "@mui/material/List";
import { ApiGet, ApiPost, ApiPut, ApiDelete } from "../helpers/API/ApiData";
import { toast, ToastContainer } from "react-toastify";
import { DataTable } from "../components/DataTable";
import { usePermissions } from "../hooks/usePermissions";
import { useContentLanguage } from "../hooks";
import { PageHeader } from "../components/PageHeader";
import { LanguageTabs } from "../components/LanguageTabs";
import { buildWreathCardColumns } from "./wreathCard/wreathCardColumns";

export default function Wreathcard() {
  const { canCreate, canEdit, canDelete } = usePermissions("/wreath");
  const [lang, setLang] = useContentLanguage();
  const [Wreathcard, setWreathcard] = useState<any>({
    currency: "%",
  }) as any;
  const [addDocument, setAddDocument] = useState(false);
  const [errors, setErrors] = useState<any>({});
  const [getAllDocument, setGetAllDocument] = useState<any[]>([]);
  const [isEditApi, setIsEditApi] = useState(false);
  const [search, setSearch] = useState<any>();
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [documentIdForEdit, setDocumentIdForEdit] = useState<any>();
  const [documentIdForDelete, setDocumentIdForDelete] = useState<any>();
  const [countPerPage, setCountPerPage] = useState(10);
  const [loader, setLoader] = useState(false);
  const [loaderForGetAll, setLoaderForGetAll] = useState(false);
  const [show, setShow] = useState(false);

  useEffect(() => {
    getAllWreathcard();
  }, [page, countPerPage, search]);

  const addDocumentModelClose = () => {
    setAddDocument(false);
    setIsEditApi(false);
    setErrors({});
    setWreathcard({ currency: "%" });
  };

  const onhandleChange = (e: any) => {
    const { name, value } = e.target;
    setWreathcard({ ...Wreathcard, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const formValidation = () => {
    let formIsValid = true;
    const errs: any = {};
    if (!Wreathcard?.name?.trim()) {
      formIsValid = false;
      errs["name"] = "*Please enter the English name";
    }
    if (!Wreathcard?.name_ger?.trim()) {
      formIsValid = false;
      errs["name_ger"] = "*Bitte geben Sie den deutschen Namen ein";
    }
    if (!Wreathcard?.percentage || Wreathcard?.percentage === "") {
      formIsValid = false;
      errs["percentage"] = "*Bitte geben Sie ein Prozentsatz ein";
    }
    setErrors(errs);
    return formIsValid;
  };

  const handleClose = () => {
    setShow(false);
  };

  const getAllWreathcard = async () => {
    setLoaderForGetAll(true);
    const params = `page=${page}&limit=${countPerPage}${search ? `&letter=${search}` : ""}`;
    await ApiGet(`wreath/find?${params}`)
      .then((res: any) => {
        setGetAllDocument(res?.data?.data);
        setCount(res?.data?.count ?? 0);
        setLoaderForGetAll(false);
      })
      .catch(() => {
        setLoaderForGetAll(false);
      });
  };

  const buildPayload = () => ({
    name: Wreathcard?.name,
    name_ger: Wreathcard?.name_ger,
    percentage: Wreathcard?.currency === "%" ? Wreathcard?.percentage : 0,
    amount: Wreathcard?.currency === "\u20AC" ? Wreathcard?.percentage : 0,
    information: Wreathcard?.information,
    information_ger: Wreathcard?.information_ger,
  });

  const addWreathcard = async () => {
    if (formValidation()) {
      setLoader(true);
      await ApiPost(`wreath/create`, buildPayload())
        .then(() => {
          toast.success("Erfolgreich hinzuf\u00fcgen");
          setAddDocument(false);
          setIsEditApi(false);
          getAllWreathcard();
          setWreathcard({ currency: "%" });
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
          setLoader(false);
        });
    }
  };

  const editWreathcard = async () => {
    if (formValidation()) {
      setLoader(true);
      await ApiPut(`wreath/update?id=${documentIdForEdit}`, buildPayload())
        .then(() => {
          toast.success("Erfolgreich geupdated");
          getAllWreathcard();
          setAddDocument(false);
          setIsEditApi(false);
          setWreathcard({ currency: "%" });
          setLoader(false);
        })
        .catch((err: any) => {
          toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
          setLoader(false);
        });
    }
  };

  const deleteWreathcard = async () => {
    await ApiDelete(`wreath/delete?id=${documentIdForDelete}`)
      .then((res: any) => {
        if (res?.status === 200) {
          toast.success("Erfolgreich gel\u00f6scht");
          setShow(false);
          setPage(1);
          getAllWreathcard();
        } else {
          toast.error(res?.data?.message || "Etwas ist schief gelaufen...");
        }
      })
      .catch((err: any) => {
        toast.error(err?.response?.data?.message || "Etwas ist schief gelaufen...");
      });
  };

  const columns = useMemo(() => buildWreathCardColumns({
    canEdit,
    canDelete,
    lang,
    onEdit: (row: any) => {
      setIsEditApi(true);
      setAddDocument(true);
      setDocumentIdForEdit(row?._id);
      setWreathcard({
        name: row?.name,
        name_ger: row?.name_ger,
        percentage:
          row?.percentage === "0" || row?.percentage === null
            ? row?.amount
            : row?.percentage,
        information: row?.information,
        information_ger: row?.information_ger,
        currency:
          row?.percentage === "0" || row?.percentage === null
            ? "\u20AC"
            : "%",
      });
    },
    onDelete: (row: any) => {
      setDocumentIdForEdit(row?._id);
      setDocumentIdForDelete(row?._id);
      setShow(true);
    },
  }), [canEdit, canDelete, lang]);

  return (
    <>
      <Paper sx={{ p: 1 }}>
        <ToastContainer />
        <PageHeader
          title="Kranzler Card"
          onSearch={(val) => { setSearch(val); setPage(1); }}
          onAdd={canCreate ? () => {
            setIsEditApi(false);
            setWreathcard({ currency: "%" });
            setErrors({});
            setAddDocument(true);
          } : undefined}
        />
        <LanguageTabs value={lang} onChange={setLang} />
        {loaderForGetAll ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <DataTable
            rows={getAllDocument || []}
            columns={columns}
            page={page}
            count={count}
            rowsPerPage={countPerPage}
            onPageChange={setPage}
            onRowsPerPageChange={setCountPerPage}
          />
        )}
      </Paper>

      <Dialog open={show} onClose={handleClose}>
        <DialogTitle sx={{ color: "error.main", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          L&ouml;schen best&auml;tigen
          <IconButton size="small" onClick={handleClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            M&ouml;chten Sie diese Kranzkarte wirklich l&ouml;schen?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="inherit">Nein</Button>
          <Button onClick={() => deleteWreathcard()} color="error" variant="contained">Ja</Button>
        </DialogActions>
      </Dialog>

      {addDocument && (
        <Dialog fullScreen open={addDocument}>
          <Toolbar>
            <IconButton
              edge="end"
              color="inherit"
              onClick={() => addDocumentModelClose()}
              aria-label="close"
              sx={{ ml: "auto" }}
            >
              <CloseIcon />
            </IconButton>
          </Toolbar>
          <List>
            <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
              {/* Dual-language column headers */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Box sx={{ width: { xs: "100%", lg: "16.66%" } }} />
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    Deutsch
                  </Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, textAlign: "center" }}>
                  <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
                    English
                  </Typography>
                </Box>
              </Box>

              {/* Name row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  Name
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    id="name_ger"
                    name="name_ger"
                    value={Wreathcard?.name_ger || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["name_ger"]}
                  </Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    size="small"
                    fullWidth
                    variant="outlined"
                    id="name"
                    name="name"
                    value={Wreathcard?.name || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["name"]}
                  </Typography>
                </Box>
              </Box>

              {/* Information row */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  Information
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    id="information_ger"
                    name="information_ger"
                    value={Wreathcard?.information_ger || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["information_ger"]}
                  </Typography>
                </Box>
                <Box sx={{ width: { xs: "100%", lg: "41.67%" }, pr: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    variant="outlined"
                    id="information"
                    name="information"
                    value={Wreathcard?.information || ""}
                    onChange={(e) => onhandleChange(e)}
                  />
                  <Typography variant="caption" color="error">
                    {errors["information"]}
                  </Typography>
                </Box>
              </Box>

              {/* Prozentsatz/Amount row (shared) */}
              <Box sx={{ display: "flex", mb: 3, flexWrap: "wrap" }}>
                <Typography sx={{ width: { xs: "100%", lg: "16.66%" }, pt: 1 }}>
                  {Wreathcard?.currency === "%" ? "Prozentsatz(%)" : "Amount(\u20AC)"}
                </Typography>
                <Box sx={{ width: { xs: "100%", lg: "83.34%" }, pr: 1 }}>
                  <Box sx={{ position: "relative" }}>
                    <Box sx={{ position: "absolute", top: 0, left: 0, zIndex: 1, width: "auto", height: "100%", pointerEvents: "none" }}>
                      <select
                        style={{ width: 130, height: "100%", background: "transparent", border: "none", padding: 0, ...(!isEditApi ? { pointerEvents: "auto" } : {}) }}
                        name="currency"
                        disabled={isEditApi}
                        value={Wreathcard?.currency ?? "%"}
                        onChange={onhandleChange}
                      >
                        <option value="%">%</option>
                        <option value="\u20AC">{"\u20AC"}</option>
                      </select>
                    </Box>
                    <TextField
                      size="small"
                      fullWidth
                      variant="outlined"
                      className="input-padding"
                      id="percentage"
                      name="percentage"
                      value={Wreathcard?.percentage || ""}
                      onChange={(e: any) => {
                        if (Wreathcard?.currency === "%" && Number(e.target.value) > 100) return;
                        onhandleChange(e);
                      }}
                      onKeyDown={(e: any) => { if (e.key === " ") e.preventDefault(); }}
                    />
                  </Box>
                  <Typography variant="caption" color="error">
                    {errors["percentage"]}
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
              {loader ? (
                <Button variant="contained" disabled>
                  {isEditApi ? "Speichern" : "Hinzuf\u00fcgen"}
                  <CircularProgress size={20} sx={{ ml: 2 }} />
                </Button>
              ) : (
                <Button
                  variant="contained"
                  onClick={() => {
                    isEditApi ? editWreathcard() : addWreathcard();
                  }}
                >
                  {isEditApi ? "Speichern" : "Hinzuf\u00fcgen"}
                </Button>
              )}
            </Box>
          </List>
        </Dialog>
      )}
    </>
  );
}
